import React, { useCallback, useEffect, useMemo, useState } from "react";
import { Button, Drawer, Tabs } from "antd";
import { CloseOutlined } from "@ant-design/icons";
import SprSotrCard, { SOTR_TAB_ITEMS, SOTR_TAB_KEYS } from "./spr-sotr-card";
import "./spr-sotr.css";

const DRAWER_WIDTH_KEY = "planOnline.sprSotr.cardDrawer.width";
const MIN_WIDTH = 520;
const MAX_WIDTH = 1200;
const DEFAULT_WIDTH = 720;
const SCREEN_MARGIN = 100;

const maxAllowedWidth = () => {
  if (typeof window === "undefined") return MAX_WIDTH;
  return Math.min(MAX_WIDTH, Math.max(MIN_WIDTH, window.innerWidth - SCREEN_MARGIN));
};

const clampWidth = (value) => {
  const max = maxAllowedWidth();
  const n = Number(value);
  if (!Number.isFinite(n)) return Math.min(DEFAULT_WIDTH, max);
  return Math.min(max, Math.max(MIN_WIDTH, Math.round(n)));
};

const loadWidth = () => {
  try {
    return clampWidth(localStorage.getItem(DRAWER_WIDTH_KEY) ?? DEFAULT_WIDTH);
  } catch {
    return DEFAULT_WIDTH;
  }
};

const saveWidth = (value) => {
  try {
    localStorage.setItem(DRAWER_WIDTH_KEY, String(clampWidth(value)));
  } catch {
    /* ignore */
  }
};

/**
 * Drawer карточки сотрудника (SprSotr).
 */
const SprSotr = ({ open, idSotr, isNew = false, onClose, onSaved }) => {
  const [size, setSize] = useState(loadWidth);
  const [activeTab, setActiveTab] = useState(SOTR_TAB_KEYS.MAIN);

  useEffect(() => {
    if (open) setActiveTab(SOTR_TAB_KEYS.MAIN);
  }, [open, idSotr, isNew]);

  useEffect(() => {
    if (!open) return undefined;
    const onWinResize = () => setSize((prev) => clampWidth(prev));
    onWinResize();
    window.addEventListener("resize", onWinResize);
    return () => window.removeEventListener("resize", onWinResize);
  }, [open]);

  const onResize = useCallback((next) => {
    setSize(clampWidth(next));
  }, []);

  const onResizeEnd = useCallback(
    (finalSize) => {
      const next = clampWidth(typeof finalSize === "number" ? finalSize : size);
      setSize(next);
      saveWidth(next);
    },
    [size]
  );

  const drawerMaxSize = maxAllowedWidth();

  const tabItems = useMemo(() => SOTR_TAB_ITEMS, []);

  const title = (
    <div className="spr-sotr-drawer__title">
      <Tabs
        className="spr-sotr-drawer__tabs"
        size="small"
        activeKey={activeTab}
        onChange={setActiveTab}
        items={tabItems}
      />
    </div>
  );

  return (
    <Drawer
      placement="right"
      title={title}
      size={size}
      maxSize={drawerMaxSize}
      open={open}
      onClose={onClose}
      destroyOnClose
      closable={false}
      rootClassName="spr-sotr-drawer"
      extra={
        <Button
          type="text"
          className="spr-sotr-drawer__close"
          icon={<CloseOutlined />}
          aria-label="Закрыть"
          title="Закрыть"
          onClick={onClose}
        />
      }
      styles={{
        body: { display: "flex", flexDirection: "column", paddingTop: 12 }
      }}
      resizable={{
        onResize,
        onResizeEnd
      }}
    >
      {open && (idSotr != null || isNew) ? (
        <SprSotrCard
          idSotr={idSotr}
          isNew={isNew}
          open={open}
          activeTab={activeTab}
          onSaved={onSaved}
          onCancel={onClose}
        />
      ) : null}
    </Drawer>
  );
};

export default SprSotr;
